import styled, { css, createGlobalStyle } from 'styled-components'
import { color } from 'ui/themes/utils'

import FocalImage from 'ui/blocks/FocalImage'
import OptimizedImage from './components/OptimizedImage'

export const HostStyles = createGlobalStyle`
  :host {
    /* Gallery */
    --product-gallery-direction: column;
    --product-gallery-gap: 12px;

    /* Large Preview */
    --product-gallery-preview-gap: 8px;
    --product-gallery-preview-border-radius: 0;

    /* Controls */
    --product-gallery-control-size: 40px;
    --product-gallery-control-padding: 0 10px;
    --product-gallery-control-border-radius: 6px;
    --product-gallery-control-background-color: ${color('white')}66;
    --product-gallery-control-icon-size: 20px;
    --product-gallery-control-icon-color: var(--color-typo, ${color('black')});
    --product-gallery-control-filter: blur(3px);

    /* Thumbnails */
    --product-gallery-thumbnail-direction: row;
    --product-gallery-thumbnail-border-radius: 6px;
    --product-gallery-thumbnail-border: 1px solid var(--color-border, ${color('Misc/Divider')});
    --product-gallery-active-thumbnail-border: 1px solid var(--color-accent-background, ${color('branding')});
    --product-gallery-thumbnail-size: 64px;
  }

  * {
    box-sizing: border-box;
  }
`

const ProductGallery = styled.div`
  display: flex;
  flex-direction: var(--product-gallery-direction);

  gap: var(--product-gallery-gap);
`

const Wrapper = styled.div`
  position: relative;
`

const Inner = styled.div`
  display: flex;
  align-items: center;

  overflow-x: auto;
  overflow-y: hidden;

  scroll-snap-type: x mandatory;

  gap: var(--horizontal-padding, 16px);

  position: relative;

  -ms-overflow-style: none;
  scrollbar-width: none;

  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({ noGap }) => noGap && css`
    gap: 0;
  `}
`

const Item = styled.div`
  scroll-snap-align: start end;
  scroll-snap-stop: always;

  min-width: 100%;
  width: 100%;
`

const Image = styled(OptimizedImage)`
  display: block;

  width: 100%;
  aspect-ratio: 1/1;

  border-radius: var(--product-gallery-preview-border-radius);

  @media (max-width: 576px) {  
    ${({ imageFitMobile }) => css`
      object-fit: ${imageFitMobile};
    `}
  }

  @media (min-width: 577px) {
    ${({ imageFitDesktop }) => css`
      object-fit: ${imageFitDesktop};
    `}
  }
`

const Controls = styled.div`
  position: absolute;

  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: var(--product-gallery-control-padding);

  pointer-events: none;

  z-index: 1000;
`

const Control = styled.button`
  width: var(--product-gallery-control-size);
  height: var(--product-gallery-control-size);

  display: flex;
  justify-content: center;
  align-items: center;

  border: none;
  border-radius: var(--product-gallery-control-border-radius);

  cursor: pointer;

  background-color: var(--product-gallery-control-background-color);
  backdrop-filter: var(--product-gallery-control-filter);

  pointer-events: all;

  svg {
    width: var(--product-gallery-control-icon-size);
    height: var(--product-gallery-control-icon-size);

    fill: var(--product-gallery-control-icon-color);
    color: var(--product-gallery-control-icon-color);
  }

  ${({ hidden }) => hidden && css`
    visibility: hidden;

    pointer-events: none;
  `}

  ${({ small }) => small && css`
    width: calc(var(--product-gallery-control-size) / 2);
    height: calc(var(--product-gallery-control-size) / 2);

    svg {
      width: calc(var(--product-gallery-control-icon-size) / 2);
      height: calc(var(--product-gallery-control-icon-size) / 2);
    }
  `}
`

const ThumbnailsInner = styled.div`
  display: flex;
  align-items: center;

  flex-direction: var(--product-gallery-thumbnail-direction);

  gap: var(--product-gallery-preview-gap);
`

const Thumbnail = styled.div`
  ${FocalImage} {
    width: var(--product-gallery-thumbnail-size);
    height: var(--product-gallery-thumbnail-size);

    border-radius: var(--product-gallery-thumbnail-border-radius);
    border: var(--product-gallery-thumbnail-border);

    cursor: pointer;

    transition: border 0.2s ease-in-out;

    ${({ isActive }) => isActive && css`
      border: var(--product-gallery-active-thumbnail-border);
    `}
  }
`

ProductGallery.Wrapper = Wrapper
ProductGallery.Inner = Inner
ProductGallery.Item = Item
ProductGallery.Image = Image
ProductGallery.Controls = Controls
ProductGallery.Control = Control
ProductGallery.ThumbnailsInner = ThumbnailsInner
ProductGallery.Thumbnail = Thumbnail

export default ProductGallery
